'use client';

import { useState } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { Reference } from '@eliadmin/core/src/api/references/references-types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { ReferenceCard } from './reference-card';
import { ReferenceSkeletonItem } from '../shared/reference-skeleton-item';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { CallToAction } from './call-to-action';

const ALL_CATEGORIES_TAB = 'all';

export const ReferencesList = ({
  locale,
  categories,
}: {
  locale: string;
  categories: { id: number; name: string }[];
}) => {
  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          orderBy: 'created_at',
          order: 'desc',
          page: pageParam,
          results: 10,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const remainingItemsInLastRow = references.length % 3;
  const callToActionColSpan = remainingItemsInLastRow === 0 ? 3 : 3 - remainingItemsInLastRow;

  return (
    <section className={cn('custom-section', 'px-0')}>
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn(
          'ea-references__tabs',
          'custom-container',
          'relative z-[5] mx-auto -mt-12 mb-24 flex flex-col items-center justify-center gap-16 md:-mt-24',
        )}
      >
        <TabsList
          className={cn(
            'ea-references__tabs-list',
            'mx-auto flex h-fit w-full max-w-[900px] flex-nowrap items-start justify-start gap-3 overflow-scroll bg-transparent',
          )}
        >
          <TabsTrigger
            value={ALL_CATEGORIES_TAB}
            className={cn('ea-references__tabs-trigger', 'rounded-full px-6 py-3.5 shadow-none')}
          >
            Všetko
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              key={category.id}
              value={String(category.id)}
              className={cn('ea-references__tabs-trigger', 'rounded-full px-6 py-3.5 shadow-none')}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          className={cn('ea-references__tabs-content', 'w-full px-6 xl:px-0')}
        >
          <div className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
            {referencesQuery.error && <p>Error: {referencesQuery.error.message}</p>}
            {referencesQuery.isLoading && <ReferencesSkeleton />}
            {referencesQuery.isSuccess && (
              <>
                {references.length === 0 ? (
                  <p className="col-span-1 pt-12 text-center text-base font-bold text-foreground sm:col-span-2 sm:text-lg lg:col-span-3 lg:text-xl">
                    Neboli nájdené žiadne referencie
                  </p>
                ) : (
                  <>
                    {references.map(reference => (
                      <ReferenceCard key={reference.id} reference={reference} />
                    ))}
                    <CallToAction colSpan={callToActionColSpan} />
                  </>
                )}
              </>
            )}
          </div>
          <div
            className={cn('mt-12 flex w-full items-center justify-center', {
              hidden: !referencesQuery.hasNextPage,
            })}
          >
            <Button
              onClick={() => referencesQuery.fetchNextPage()}
              isLoading={referencesQuery.isFetchingNextPage}
              variant={'secondary'}
              className="bg-primary-foreground"
            >
              Zobraziť viac
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
};

const ReferencesSkeleton = () => {
  return (
    <div
      className={cn(
        'animate-pulse',
        'col-span-1 grid w-full grid-cols-1 gap-8 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3 lg:gap-10',
      )}
    >
      <ReferenceSkeletonItem />
      <ReferenceSkeletonItem />
      <ReferenceSkeletonItem />
      <ReferenceSkeletonItem />
      <ReferenceSkeletonItem />
    </div>
  );
};
